import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import AccordionReact from 'devextreme-react/accordion';
import FormField from '../../../dynamicFormBuilder/FormField';
import { GridLayout } from "../../../common/GridLayout";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import Toolbar, { Item } from "devextreme-react/toolbar";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
    Lookup,
} from "devextreme-react/data-grid";
import { SearchLayout } from './Layout';
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";
import '../../../common/css/searchPage.css';

const routes = data.MoveSublotInventory;
const MOVE_ROUTE = '/wm/move-sublot-inventory-move';

const searchpanel = [
    {
        ID: 1,
        Title: 'Search Filters'
    }
];

// Find screen for Move Sublot Inventory: search sublot inventory, multi-select
// the sublots to move (all from a single customer), then Select carries them to
// the Move screen via router state. Search-driven — no data loads on mount.
const MoveSublotInventoryList = (props) => {
    const history = useHistory();
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const formRefs = useRef({});
    const [selectedItems, setSelectedItems] = useState([]);
    const dataGrid = useRef(null);

    const allowedPageSizes = [10, 20, 100, 500, 1000, "all"];
    const gridKey = "B2A8F4C1-6D3E-4A79-9E52-7F1C0B4D8E63";
    const [currentLayout, setCurrentLayout] = useState("Default");
    const loadState = useCallback(() => { return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout); }, [currentLayout]);
    const saveState = useCallback((state) => { GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); }, [gridKey, currentLayout]);

    const initialFormFields = {
        customerCode: '',
        itemCode: '',
        lotCode: '',
        sublotCode: '',
    };
    const [formFields, setformFields] = useState(initialFormFields);
    const formFieldsRef = useRef(formFields);
    const [isSelectDisabled, setIsSelectDisabled] = useState(true);

    // Grid stays unbound until the user clicks Search.
    const [resultData, setResultData] = useState(null);

    // Keep the ref in sync so createStore loadParams read the latest filter values.
    useEffect(() => {
        formFieldsRef.current = formFields;
    }, [formFields]);

    const handleFormLoad = useCallback(() => {
        // All search fields are free-text; no select options to load.
    }, []);

    const handleFieldChange = useCallback((name, value) => {
        // Field values are pushed into formFields state by FormField itself.
    }, []);

    const loadDatasource = useCallback(() => {
        const store = createStore({
            key: 'Key',
            loadParams: {
                'filter.CustomerCode': () => formFieldsRef.current.customerCode || '',
                'filter.ItemCode': () => formFieldsRef.current.itemCode || '',
                'filter.LotCode': () => formFieldsRef.current.lotCode || '',
                'filter.SublotCode': () => formFieldsRef.current.sublotCode || '',
            },
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.GetMoveSublotInventory}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store: store });
    }, [accessToken]);

    // Enable the Select button only while at least one grid row is selected.
    const onGridSelectionChanged = ({ selectedRowsData }) => {
        setIsSelectDisabled(!selectedRowsData || selectedRowsData.length === 0);
    };

    const handleSearch = (e) => {
        e.event.stopPropagation();
        const f = formFieldsRef.current || {};
        // Require at least one filter — never load the full sublot inventory unfiltered.
        if (!f.customerCode && !f.itemCode && !f.lotCode && !f.sublotCode) {
            notify({ message: 'Please enter at least one search filter.', width: 450 }, 'warning', 2500);
            return;
        }
        // Assigning a fresh DataSource re-binds the grid and triggers a load with the
        // current filter values; no explicit grid.refresh() call is needed.
        setResultData(loadDatasource());
    };

    const handleReset = (e) => {
        e.event.stopPropagation();
        formFieldsRef.current = initialFormFields;
        setformFields(initialFormFields);
    };

    const selectionChanged = useCallback(
        (e) => {
            let newItems = [...selectedItems];
            e.removedItems.forEach((item) => {
                const index = newItems.indexOf(item);
                if (index >= 0) {
                    newItems.splice(index, 1);
                }
            });
            if (e.addedItems.length) {
                newItems = [...newItems, ...e.addedItems];
            }
            setSelectedItems(newItems);
        },
        [selectedItems, setSelectedItems],
    );

    const handleSelect = () => {
        const grid = dataGrid.current && dataGrid.current.instance ? dataGrid.current.instance() : null;
        const selected = grid ? grid.getSelectedRowsData() : [];
        if (!selected || selected.length === 0) {
            notify({ message: 'Please select at least one sublot.', width: 450 }, 'error', 2500);
            return;
        }
        const distinctCustomers = [...new Set(selected.map((r) => r.RIdentity))];
        if (distinctCustomers.length > 1) {
            notify({ message: 'Please select sublots from a single customer.', width: 450 }, 'error', 2500);
            return;
        }
        // The customer must have a WM Adjustment Profile to create the adjustment; a blank AdjProfile
        // (from the search read) means it cannot be moved. Block here so it never reaches the Move screen.
        const noProfile = selected.filter((r) => !r.AdjProfile);
        if (noProfile.length > 0) {
            const codes = [...new Set(noProfile.map((r) => r.SublotCode))].join(', ');
            notify({ message: `The customer has no WM Adjustment Profile, so these sublot(s) cannot be moved: ${codes}.`, width: 550 }, 'error', 4000);
            return;
        }
        history.push(MOVE_ROUTE, { sublots: selected });
    };

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <div id="accordion" className="search-page-accordion">
                    <AccordionReact
                        dataSource={searchpanel}
                        collapsible={true}
                        multiple={false}
                        animationDuration={300}
                        selectedItems={selectedItems}
                        onSelectionChanged={e => {
                            if (e.currentTarget === e.target) {
                                selectionChanged(e);
                            }
                        }}
                        itemTitleRender={() => (
                            <Toolbar className="accordian-toolbar">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2
                                            }}
                                        >
                                            Enter Your Query For Sublot Inventory
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleSearch} text="Search" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                            </Toolbar>
                        )}
                        itemRender={() => (
                            <FormField
                                currrentUser={currentUser}
                                layoutId='7B3E1A9C-4F2D-4C81-A6E5-2D9B0C7F3A18'
                                layoutName='WMMoveSublotInventorySearchForm'
                                defaultFormSchema={SearchLayout}
                                formFields={formFields}
                                setformFields={setformFields}
                                handleFieldValueChange={handleFieldChange}
                                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                                formRefs={formRefs}
                                onFormLoad={handleFormLoad}
                                noSpacingBeforeFirstRow={true}
                            />
                        )}
                        id="accordion-container" className="accordion-height"
                    />
                </div>
                <div className="search-page-grid-container">
                    <DataGrid
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        ref={dataGrid}
                        onSelectionChanged={onGridSelectionChanged}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        dataSource={resultData}
                        showBorders={true}
                        hoverStateEnabled={true}
                        className="search-page-grid"
                        remoteOperations={true}
                        searchEnabled={true}
                        columnAutoWidth={true}
                        noDataText="Search to list sublot inventory."
                    >
                        <ColumnChooser
                            enabled={true}
                            title="Choose Column"
                            width={290}
                            height={290}
                            sortOrder="asc"
                        >
                            <ColumnChooserSearch
                                enabled={true}
                                editorOptions={{ placeholder: 'Search column' }} />
                        </ColumnChooser>
                        <Export enabled={true} allowExportSelectedData={true} />
                        <Selection mode="multiple" />
                        <Sorting visible={true} mode="multiple" />
                        <Paging defaultPageSize={10} />
                        <Pager
                            visible={true}
                            showPageSizeSelector={true}
                            allowedPageSizes={allowedPageSizes}
                            showNavigationButtons={true}
                        />
                        <FilterRow visible={true} />
                        <FilterPanel visible={true} />
                        <GroupPanel visible={true} />
                        <Grouping autoExpandAll={false} />
                        <StateStoring
                            enabled={true}
                            type="custom"
                            customLoad={loadState}
                            storageKey={gridKey}
                            customSave={saveState}
                        />
                        <GridToolbar>
                            <GridToolbarItem location="before">
                                <Button
                                    onClick={handleSelect}
                                    text="Select"
                                    type="default"
                                    disabled={isSelectDisabled}
                                ></Button>
                            </GridToolbarItem>
                            <GridToolbarItem location="after">
                                <GridLayoutManager
                                    gridKey={gridKey}
                                    dataGrid={dataGrid}
                                    currentLayout={currentLayout}
                                    setCurrentLayout={setCurrentLayout}
                                    accessToken={accessToken}
                                />
                            </GridToolbarItem>
                            <GridToolbarItem name="exportButton" location="after" />
                            <GridToolbarItem name="columnChooserButton" location="after" />
                        </GridToolbar>

                        <Column dataType="string" caption="Customer" dataField="CustomerName" />
                        <Column dataType="string" caption="Customer Code" dataField="Customer" visible={false} />
                        <Column dataType="string" caption="Item" dataField="ItemCode" />
                        <Column dataType="string" caption="Lot" dataField="LotCode" />
                        <Column dataType="string" caption="Sublot" dataField="SublotCode" />
                        <Column dataType="string" caption="Description1" dataField="SublotDescription1" />
                        <Column dataType="string" caption="Status" dataField="StatusSublot">
                            <Lookup
                                dataSource={[{ id: 'A', text: 'Active' }, { id: 'I', text: 'Inactive' }]}
                                displayExpr="text"
                                valueExpr="id" />
                        </Column>
                        <Column dataType="date" caption="Date Available" dataField="DateAvailableSublot" format="MM/dd/yyyy" />
                        <Column dataType="number" caption="Net Available" dataField="NetAvailableSublot" format="#,##0.00" />
                        <Column dataType="number" caption="Available" dataField="SublotAvailable" format="#,##0.00" />
                        <Column dataType="number" caption="On Hand" dataField="OnHandSublot" format="#,##0.00" />
                        <Column dataType="string" caption="Record #" dataField="RecordNumber" />

                        {/* Customer's WM Adjustment Profile (legacy AdjProfile). Available via the column
                            chooser. A blank value means the customer cannot adjust — see handleSelect. */}
                        <Column dataType="string" caption="Adjustment Profile" dataField="AdjProfile" visible={false} />

                        {/* Additional VW_WMSUBLOTVIEW buckets (legacy SublotView parity). Hidden by
                            default; available through the column chooser and saved layouts. */}
                        <Column dataType="string" caption="Description2" dataField="SublotDescription2" visible={false} />
                        <Column dataType="date" caption="Date Introduced" dataField="SublotDateIntroduced" format="MM/dd/yyyy" visible={false} />
                        <Column dataType="date" caption="Date Expiration" dataField="DateExpirationSublot" format="MM/dd/yyyy" visible={false} />
                        <Column dataType="number" caption="Net Weight" dataField="NetWeightQuantitySublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Gross Weight" dataField="GrossWeightQuantitySublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="In Receiving" dataField="InReceivingSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="In Shipping" dataField="InShippingSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Pending Rec" dataField="PendingRecSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Pending Ship" dataField="PendingShipSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Future Rec" dataField="FutureRecSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Future Ship" dataField="FutureShipSublot" format="#,##0.00" visible={false} />
                        <Column dataType="number" caption="Unavailable" dataField="UnavailableSublot" format="#,##0.00" visible={false} />

                        {/* Technical key columns hidden per convention. */}
                        <Column dataField="ItemRowId" visible={false} showInColumnChooser={false} />
                        <Column dataField="LotRowId" visible={false} showInColumnChooser={false} />
                        <Column dataField="SublotRowId" visible={false} showInColumnChooser={false} />
                        <Column dataField="RIdentity" visible={false} showInColumnChooser={false} />
                        <Column dataField="ItemType" visible={false} showInColumnChooser={false} />
                    </DataGrid>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(MoveSublotInventoryList);
